// src/components/dashboard/ContentSelector.jsx - Component for manual content selection
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
    MagnifyingGlassIcon,
    XMarkIcon,
    DocumentTextIcon,
    ClockIcon,
    LinkIcon,
    EyeIcon,
    SparklesIcon,
    TagIcon,
    CalendarIcon
} from '@heroicons/react/24/outline';

function ContentSelector({ onSelectContent, onClose, isGenerating }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedContent, setSelectedContent] = useState(null);
    const [filter, setFilter] = useState('all'); // 'all', 'recent', 'unused'

    // Fetch cached content
    const { data: cachedContent, isLoading } = useQuery(
        ['cachedContent', filter],
        () => {
            const params = new URLSearchParams({
                limit: '50',
                sortBy: filter === 'recent' ? 'metadata.lastFetched' : 'updatedAt',
                includeStale: filter === 'all' ? 'true' : 'false'
            });
            const token = localStorage.getItem('token');
            return fetch(`/api/content-cache?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }).then(res => res.json());
        }
    );

    const contentList = cachedContent?.data || [];

    // Filter content based on search term
    const filteredContent = contentList.filter(content => {
        if (!searchTerm) return true;

        const searchLower = searchTerm.toLowerCase();
        return (
            content.title?.toLowerCase().includes(searchLower) ||
            content.summary?.toLowerCase().includes(searchLower) ||
            content.sourceUrl?.toLowerCase().includes(searchLower) ||
            content.keywords?.some(keyword => keyword.toLowerCase().includes(searchLower))
        );
    }).filter(content => {
        if (filter === 'unused') {
            return !content.lastUsedForPost || content.usageCount === 0;
        }
        if (filter === 'recent') {
            const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
            return new Date(content.metadata?.lastFetched) > oneDayAgo;
        }
        return true;
    });

    const handleContentSelect = (content) => {
        if (selectedContent?._id === content._id) {
            setSelectedContent(null);
        } else {
            setSelectedContent(content);
        }
    };

    const handleGenerate = () => {
        if (selectedContent) {
            onSelectContent(selectedContent._id);
        }
    };

    const formatTimeAgo = (date) => {
        const now = new Date();
        const diff = now - new Date(date);
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days}d ago`;
        if (hours > 0) return `${hours}h ago`;
        return 'Now';
    };

    const isContentStale = (date, maxHours = 24) => {
        const diff = new Date() - new Date(date);
        return diff > (maxHours * 60 * 60 * 1000);
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    onClick={onClose}
                ></div>

                {/* Modal panel */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    {/* Header */}
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                    Select Content for Post Generation
                                </h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Choose specific content to create a targeted social media post
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                    </div>

                    {/* Search and Filters */}
                    <div className="bg-gray-50 px-4 py-3 sm:px-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                            {/* Search */}
                            <div className="relative flex-1 max-w-md">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search content..."
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                            </div>

                            {/* Filters */}
                            <div className="flex space-x-2">
                                <select
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    className="block pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                >
                                    <option value="all">All Content</option>
                                    <option value="recent">Recent (24h)</option>
                                    <option value="unused">Unused</option>
                                </select>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="mt-3 flex items-center space-x-4 text-sm text-gray-500">
                            <span>{filteredContent.length} items found</span>
                            {selectedContent && (
                                <span className="text-blue-600">
                                    • "{selectedContent.title}" selected
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Content List */}
                    <div className="px-4 py-4 sm:px-6" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        ) : filteredContent.length === 0 ? (
                            <div className="text-center py-8">
                                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No content found</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {searchTerm ? 'Try a different search term' : 'Add some content sources and cache them first'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredContent.map((content) => {
                                    const isSelected = selectedContent?._id === content._id;
                                    const isStale = isContentStale(content.metadata?.lastFetched);

                                    return (
                                        <div
                                            key={content._id}
                                            onClick={() => handleContentSelect(content)}
                                            className={`relative border rounded-lg p-4 cursor-pointer transition-all ${
                                                isSelected
                                                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            {/* Selection indicator */}
                                            {isSelected && (
                                                <div className="absolute top-2 right-2">
                                                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Content header */}
                                            <div className="flex items-start justify-between mb-2">
                                                <h4 className="text-sm font-medium text-gray-900 line-clamp-2 pr-8">
                                                    {content.title}
                                                </h4>
                                            </div>

                                            {/* Content preview */}
                                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                                {content.summary || content.content?.substring(0, 150)}...
                                            </p>

                                            {/* Content metadata */}
                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                <div className="flex items-center space-x-3">
                                                    <span className="flex items-center">
                                                        <ClockIcon className="h-3 w-3 mr-1" />
                                                        {formatTimeAgo(content.metadata?.lastFetched)}
                                                        {isStale && <span className="ml-1 text-yellow-600">(stale)</span>}
                                                    </span>

                                                    <span className="flex items-center">
                                                        <EyeIcon className="h-3 w-3 mr-1" />
                                                        {content.usageCount || 0} uses
                                                    </span>

                                                    <span>{content.sections?.length || 0} sections</span>
                                                </div>

                                                <a
                                                    href={content.sourceUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="flex items-center text-blue-500 hover:text-blue-600"
                                                >
                                                    <LinkIcon className="h-3 w-3 mr-1" />
                                                    Source
                                                </a>
                                            </div>

                                            {/* Keywords */}
                                            {content.keywords && content.keywords.length > 0 && (
                                                <div className="mt-2 flex flex-wrap gap-1">
                                                    <TagIcon className="h-3 w-3 text-gray-400 mt-0.5" />
                                                    {content.keywords.slice(0, 5).map((keyword, i) => (
                                                        <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                                            {keyword}
                                                        </span>
                                                    ))}
                                                    {content.keywords.length > 5 && (
                                                        <span className="text-gray-400 text-xs">
                                                            +{content.keywords.length - 5} more
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Last used info */}
                                            {content.lastUsedForPost && (
                                                <div className="mt-2 flex items-center text-xs text-gray-400">
                                                    <CalendarIcon className="h-3 w-3 mr-1" />
                                                    Last used: {new Date(content.lastUsedForPost).toLocaleDateString()}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            onClick={handleGenerate}
                            disabled={!selectedContent || isGenerating}
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isGenerating ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <img src="/images/Bob_only.svg" alt="PostMgr" className="h-4 w-4" />
                                    Generate Post
                                </>
                            )}
                        </button>
                        <button
                            onClick={onClose}
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ContentSelector;